import { useEffect, useMemo, useState } from 'react'

import { subscribeRealtimeOrders } from '@/api/orders/subscribe-realtime-orders'
import type { OrderRealtimeChunkType } from '@/types/order-realtime.types'

type UseRealtimeOrdersParams = {
  merchantIds: string[]
  statuses: string[]
  enabled?: boolean
}

function useRealtimeOrders({
  merchantIds,
  statuses,
  enabled = true,
}: UseRealtimeOrdersParams) {
  const [orders, setOrders] = useState<OrderRealtimeChunkType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const merchantIdsKey = merchantIds.join(',')
  const statusesKey = statuses.join(',')

  const stableMerchantIds = useMemo(
    () => merchantIdsKey.split(',').filter(Boolean),
    [merchantIdsKey]
  )

  const stableStatuses = useMemo(
    () => statusesKey.split(',').filter(Boolean),
    [statusesKey]
  )

  useEffect(() => {
    if (!enabled || stableMerchantIds.length === 0) {
      setOrders([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    const unsubscribe = subscribeRealtimeOrders({
      merchantIds: stableMerchantIds,
      statuses: stableStatuses,
      onOrders: (realtimeOrders) => {
        setOrders(realtimeOrders)
        setIsLoading(false)
      },
    })

    return unsubscribe
  }, [enabled, stableMerchantIds, stableStatuses])

  return { orders, isLoading, setOrders }
}

export { useRealtimeOrders }
